//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ZeroAddress } from 'ethers'
import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import {
  EXCHANGE_RATE_ONE,
  ExchangeRateTypeEnum,
  ExchangeRateTypeToString,
  PoolStateToString,
  yupErc20WeiRequired,
  yupEthAddrRequired,
} from './Enums'
import { PoolBaseOverviewCTO, PoolBaseOverviewCTOSchemaValues } from './PoolBaseCTO'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

const PoolBaseOverviewCTOSchemaInfer = yup.object({
  ...PoolBaseOverviewCTOSchemaValues,
  borrowerManagerAddr: yupEthAddrRequired
    .label('Issuer Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'borrowerManagerAddr',
        description: 'The wallet address of the Advisor.',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  poolAdminAddr: yupEthAddrRequired
    .label('Protocol Administrator Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAdminAddr',
        description: 'The wallet address of the Pool Administrator',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
})
export const PoolLytOverviewCTOSchema = yup
  .object({
    feeCollectorAddress: yupEthAddrRequired
      .label('Fee Collector Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'feeCollectorAddress',
          stringType: StringTypeEnum.ETH_ADDR,
          description: 'The smart-contract address for the Fee Collector. ',
        })
      ),

    state: yup
      .number()
      .required()
      .default(0)
      .label('State')
      .meta(
        toFieldMetaData({
          propName: 'state',
          description: 'The State of the Loan Vault',
          stateToString: PoolStateToString,
        })
      ),

    totalAssetsDeposited: yupErc20WeiRequired
      .label('Total Assets Deposited')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsDeposited',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 13,
          description:
            'The total amount of assets deposited since vault inception. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    totalAssetsWithdrawn: yupErc20WeiRequired
      .label('Total Assets Withdrawn')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsWithdrawn',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 14,
          description:
            'The total amount of assets withdrawn since vault inception. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    exchangeRate: yupErc20WeiRequired
      .label('Exchange Rate')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 15,
          description:
            'The current exchange rate at which Vault Tokens and Assets will be exchanged. The Exchange Rate increases by the Daily Interest Rate each day. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),

    exchangeRateAtSetDay: yupErc20WeiRequired
      .label('Exchange Rate At Set Day')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtSetDay',
          description: 'TODO',
          stringType: StringTypeEnum.ETH_DAY_NUMBER,
        })
      ),

    exchangeRateChangeRate: yupErc20WeiRequired
      .label('Exchange Rate Change Rate')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateChangeRate',
          description: 'TODO',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),

    exchangeRateCompoundingRate: yupErc20WeiRequired
      .label('Exchange Rate Compounding Rate')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateCompoundingRate',
          description: 'TODO',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),
    exchangeRateAtMaturity: yupErc20WeiRequired
      .label('Exchange Rate At Maturity')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtMaturity',
          description: 'TODO',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),
    exchangeRateSetDay: yup
      .number()
      .label('Exchange Rate Set Day')
      .required()
      .default(0)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateSetDay',
          description: 'Day the exchange rate was set',
          stringType: StringTypeEnum.ETH_DAY_NUMBER,
        })
      ),
    exchangeRateMaturityDay: yup
      .number()
      .label('Exchange Rate Maturity Day')
      .required()
      .default(0)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateMaturityDay',
          description: 'TODO',
          stringType: StringTypeEnum.ETH_DAY_NUMBER,
        })
      ),
    exchangeRateType: yup
      .mixed<ExchangeRateTypeEnum>()
      .oneOf(Object.values(ExchangeRateTypeEnum) as number[])
      .label('Exchange Rate Type')
      .defined()
      .default(ExchangeRateTypeEnum.Unset)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateType',
          description: 'Type of pool ',
          stateToString: ExchangeRateTypeToString,
        })
      ),
    totalInterestAccrued: yupErc20WeiRequired
      .label('Total Interest Accrued')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalInterestAccrued',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 12,
          description:
            'The total amount of interest accrued to date. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalShares: yupErc20WeiRequired
      .label('Total Vault Tokens Outstanding')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalShares',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 16,
          description:
            'The total number of vault tokens outstanding. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),

    totalAssets: yupErc20WeiRequired
      .label('Total Assets')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssets',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 11,
          description:
            'The total amount of assets. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    treasuryWalletAddr: yupEthAddrRequired
      .label('Treasury Wallet Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'treasuryWalletAddr',
          description: 'The wallet address of the Treasury. ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    interestRate: yupErc20WeiRequired
      .label('Interest Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'interestRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 10,
          description:
            'The current annualized interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
          stringType: StringTypeEnum.APR,
        })
      ),
    indicativeInterestRate: yupErc20WeiRequired
      .label('Indicative Interest Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'indicativeInterestRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 18,
          description: 'The target rate of return for a dynamic vault',
          stringType: StringTypeEnum.APR,
        })
      ),
    collateralRate: yupErc20WeiRequired
      .label('Collateral Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'collateralRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 19,
          description: 'The rate of return for the underlying collateral',
          stringType: StringTypeEnum.APR,
        })
      ),
    totalValueOfReservesInSierra: yupErc20WeiRequired
      .label('Total Value of Reserves in Sierra')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalValueOfReservesInSierra',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 20,
          description:
            'Total value of reserves in Sierra including lytIssuerVaultBalance, OT vaults, and lytPoolBalance',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
  })
  .concat(PoolBaseOverviewCTOSchemaInfer)
  .label('PoolLytOverviewCTO')
  .meta(toFieldMetaData({ propName: 'PoolLytOverviewCTO', description: 'PoolLytOverviewCTOSchema' }))

export interface PoolLytOverviewCTO extends PoolBaseOverviewCTO {
  feeCollectorAddress: EthAddr
  treasuryWalletAddr: EthAddr
  exchangeRateType: ExchangeRateTypeEnum
  state: number
  totalAssetsDeposited: ERC20Wei
  totalAssetsWithdrawn: ERC20Wei

  exchangeRate: ERC20Wei
  exchangeRateAtSetDay: ERC20Wei
  exchangeRateSetDay: number
  exchangeRateChangeRate: ERC20Wei
  exchangeRateCompoundingRate: ERC20Wei
  exchangeRateAtMaturity: ERC20Wei
  exchangeRateMaturityDay: number

  totalInterestAccrued: ERC20Wei
  totalShares: ERC20Wei
  totalAssets: ERC20Wei
  interestRate: ERC20Wei

  indicativeInterestRate: ERC20Wei
  collateralRate: ERC20Wei
  totalValueOfReservesInSierra: ERC20Wei
}

type PoolLytOverviewCTOInfer = InferType<typeof PoolLytOverviewCTOSchema>
export const _testAPoolLytOverview: PoolLytOverviewCTO = {} as unknown as PoolLytOverviewCTOInfer
export const _testBPoolLytOverview: PoolLytOverviewCTOInfer = {} as unknown as PoolLytOverviewCTO
